import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';
import api from '../services/api';
import ImageCapture from '../components/ImageCapture';
import IdentityVerifyNotice from '../components/IdentityVerifyNotice';
import './Login.css';
import './Register.css';

function ResetPassword() {
    const { appName, requireFacePhoto, photoCheckEnabled } = useBranding();
    const navigate = useNavigate();

    const [step, setStep] = useState(1); // 1 = กรอกเลขบัตรเพื่อตรวจสอบ, 2 = อัพโหลดหลักฐาน + ตั้งรหัสใหม่
    const [cid, setCid] = useState('');
    const [maskedName, setMaskedName] = useState('');
    const [checking, setChecking] = useState(false);

    const [idCardFile, setIdCardFile] = useState(null);
    const [idCardPreview, setIdCardPreview] = useState('');
    const [faceFile, setFaceFile] = useState(null);       // รูปถ่ายตนเองถือบัตรประชาชน
    const [facePreview, setFacePreview] = useState('');
    const [phone, setPhone] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [note, setNote] = useState('');

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [successViaEmail, setSuccessViaEmail] = useState(false);
    const [loading, setLoading] = useState(false);

    // รีเซ็ตเองผ่านอีเมล — มีให้เลือกเฉพาะบัญชีที่ยืนยันอีเมลแล้ว
    const [canEmail, setCanEmail] = useState(false);
    const [maskedEmail, setMaskedEmail] = useState('');
    const [method, setMethod] = useState(null); // null = ยังไม่เลือก, 'email', 'manual'
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [info, setInfo] = useState('');

    const handleCheck = async (e) => {
        e.preventDefault();
        setError('');
        const cleanCid = cid.replace(/\D/g, '');
        if (!/^\d{13}$/.test(cleanCid)) {
            setError('กรุณากรอกเลขบัตรประชาชนให้ครบ 13 หลัก');
            return;
        }
        setChecking(true);
        try {
            const res = await api.post('/auth/reset-request/check', { cid: cleanCid });
            if (res.data.found) {
                setMaskedName(res.data.masked_name || '');
                setCanEmail(!!res.data.can_email);
                setMaskedEmail(res.data.masked_email || '');
                setMethod(res.data.can_email ? null : 'manual');
                setCodeSent(false);
                setCode('');
                setInfo('');
                setStep(2);
            } else {
                setError('ไม่พบบัญชีผู้ใช้ของเลขบัตรนี้ในระบบ');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'ไม่พบบัญชีผู้ใช้ของเลขบัตรนี้ในระบบ');
        } finally {
            setChecking(false);
        }
    };

    const handleIdCardSelect = (file) => {
        if (!file) return;
        if (file.type && !['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)) {
            setError('รองรับเฉพาะรูปภาพ (JPG, PNG, WEBP, HEIC)');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setError('รูปบัตรต้องไม่เกิน 8MB');
            return;
        }
        setError('');
        setIdCardFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setIdCardPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const clearIdCard = () => {
        setIdCardFile(null);
        setIdCardPreview('');
    };

    // รูปถ่ายตนเองถือบัตรประชาชน (เห็นหน้า + ข้อมูลบัตรชัดเจน)
    const handleFaceSelect = (file) => {
        if (!file) return;
        if (file.type && !['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)) {
            setError('รองรับเฉพาะรูปภาพ (JPG, PNG, WEBP, HEIC)');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setError('รูปถ่ายต้องไม่เกิน 8MB');
            return;
        }
        setError('');
        setFaceFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setFacePreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const clearFace = () => {
        setFaceFile(null);
        setFacePreview('');
    };

    const handleSendCode = async () => {
        setError('');
        setInfo('');
        setSendingCode(true);
        try {
            const res = await api.post('/auth/reset-request/email/send', { cid: cid.replace(/\D/g, '') });
            setCodeSent(true);
            setInfo(res.data.message || 'ส่งรหัสไปที่อีเมลแล้ว');
        } catch (err) {
            setError(err.response?.data?.error || 'ส่งรหัสไม่สำเร็จ');
        } finally {
            setSendingCode(false);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!/^\d{6}$/.test(code)) {
            setError('กรุณากรอกรหัส 6 หลักจากอีเมล');
            return;
        }
        if (newPassword.length < 4) {
            setError('รหัสผ่านใหม่ต้องมีอย่างน้อย 4 ตัวอักษร');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('รหัสผ่านยืนยันไม่ตรงกัน');
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/auth/reset-request/email/confirm', {
                cid: cid.replace(/\D/g, ''),
                code,
                new_password: newPassword
            });
            setSuccessViaEmail(true);
            setSuccess(res.data.message || 'ตั้งรหัสผ่านใหม่เรียบร้อยแล้ว');
            setTimeout(() => navigate('/login'), 4000);
        } catch (err) {
            setError(err.response?.data?.error || 'ตั้งรหัสผ่านใหม่ไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    };

    const backToStep1 = () => { setStep(1); setError(''); setInfo(''); };

    const errorAlert = error && (
        <div className="alert alert-error">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            {error}
        </div>
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const phoneDigits = phone.replace(/\D/g, '');
        if (phoneDigits.length < 9 || phoneDigits.length > 10) {
            setError('กรุณากรอกเบอร์โทรที่เคยให้ไว้ให้ถูกต้อง (9-10 หลัก)');
            return;
        }
        if (requireFacePhoto && !faceFile) {
            setError('กรุณาถ่ายรูปตนเองถือบัตรประชาชน ให้เห็นใบหน้าและข้อมูลบัตรชัดเจน');
            return;
        }
        if (!idCardFile) {
            setError('กรุณาถ่าย/แนบรูปบัตรประจำตัวประชาชน (เฉพาะบัตร)');
            return;
        }
        if (newPassword.length < 4) {
            setError('รหัสผ่านใหม่ต้องมีอย่างน้อย 4 ตัวอักษร');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('รหัสผ่านยืนยันไม่ตรงกัน');
            return;
        }

        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('cid', cid.replace(/\D/g, ''));
            fd.append('phone', phoneDigits);
            fd.append('new_password', newPassword);
            fd.append('note', note.trim());
            fd.append('id_card', idCardFile);
            if (faceFile) fd.append('face_image', faceFile);

            const res = await api.post('/auth/reset-request', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSuccess(res.data.message || 'ส่งคำขอรีเซ็ตรหัสผ่านเรียบร้อยแล้ว');
            setTimeout(() => navigate('/login'), 5000);
        } catch (err) {
            setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการส่งคำขอ');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card register-card">
                    <div className="login-header">
                        <div className="login-logo">
                            <img
                                src={`${process.env.PUBLIC_URL}/icon-512.png`}
                                alt={appName}
                                className="login-logo-icon"
                            />
                            <h1 className="login-title">{appName}</h1>
                        </div>
                        <p className="login-subtitle">ขอรีเซ็ตรหัสผ่าน</p>
                        <p className="login-hospital">สำหรับผู้ที่มีบัญชีอยู่แล้ว</p>
                    </div>

                    {success ? (
                        <div className="register-success">
                            <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <h3>{successViaEmail ? 'เปลี่ยนรหัสผ่านสำเร็จ' : 'ส่งคำขอเรียบร้อย'}</h3>
                            <p>{success}</p>
                            <p className="register-redirect">
                                {successViaEmail
                                    ? 'กำลังกลับไปหน้าเข้าสู่ระบบ...'
                                    : 'เจ้าหน้าที่จะตรวจสอบหลักฐานและเปลี่ยนรหัสให้ — กำลังกลับไปหน้าเข้าสู่ระบบ...'}
                            </p>
                            <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'center' }}>
                                <Link to="/login" className="steam-btn steam-btn-primary">ไปหน้าเข้าสู่ระบบ</Link>
                            </div>
                        </div>
                    ) : step === 1 ? (
                        <form onSubmit={handleCheck} className="login-form">
                            {error && (
                                <div className="alert alert-error">
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                    </svg>
                                    {error}
                                </div>
                            )}
                            <div className="form-group">
                                <label className="form-label">เลขบัตรประชาชน (ที่ใช้เข้าสู่ระบบ) <span className="req">*</span></label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="steam-input"
                                    value={cid}
                                    onChange={(e) => setCid(e.target.value.replace(/\D/g, '').slice(0, 13))}
                                    placeholder="เลขบัตรประชาชน 13 หลัก"
                                    required
                                    autoFocus
                                />
                                <span className="form-hint-inline">ระบบจะตรวจสอบว่าเลขบัตรนี้มีบัญชีอยู่จริงก่อน</span>
                            </div>
                            <button type="submit" className="steam-btn steam-btn-primary login-btn" disabled={checking}>
                                {checking ? (
                                    <>
                                        <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                                        กำลังตรวจสอบ...
                                    </>
                                ) : 'ตรวจสอบบัญชี'}
                            </button>
                        </form>
                    ) : method === null ? (
                        <div className="login-form">
                            {errorAlert}
                            <div className="alert alert-success" style={{ marginBottom: 12 }}>
                                พบบัญชีของเลขบัตร <strong>{cid}</strong>{maskedName ? <> — ชื่อ <strong>{maskedName}</strong></> : ''}
                            </div>
                            <p style={{ margin: '0 0 12px' }}>เลือกวิธียืนยันตัวตนเพื่อตั้งรหัสผ่านใหม่</p>
                            <button type="button" className="steam-btn steam-btn-primary login-btn" onClick={() => { setMethod('email'); setError(''); }}>
                                รับรหัสทางอีเมล {maskedEmail} (เปลี่ยนได้ทันที)
                            </button>
                            <button
                                type="button"
                                className="steam-btn steam-btn-secondary login-btn"
                                style={{ marginTop: 8 }}
                                onClick={() => { setMethod('manual'); setError(''); }}
                            >
                                เข้าอีเมลนี้ไม่ได้ — ยืนยันด้วยรูปบัตร (รอเจ้าหน้าที่ตรวจสอบ)
                            </button>
                            <button type="button" className="steam-btn steam-btn-secondary login-btn" style={{ marginTop: 8 }} onClick={backToStep1}>
                                ย้อนกลับ
                            </button>
                        </div>
                    ) : method === 'email' ? (
                        <form onSubmit={handleEmailSubmit} className="login-form">
                            {errorAlert}
                            {info && <div className="alert alert-success" style={{ marginBottom: 12 }}>{info}</div>}

                            {!codeSent ? (
                                <>
                                    <p style={{ margin: '0 0 12px' }}>
                                        ระบบจะส่งรหัส 6 หลักไปที่อีเมล <strong>{maskedEmail}</strong> ที่ผูกกับบัญชีของคุณ
                                    </p>
                                    <button type="button" className="steam-btn steam-btn-primary login-btn" onClick={handleSendCode} disabled={sendingCode}>
                                        {sendingCode ? 'กำลังส่งรหัส...' : 'ส่งรหัสไปที่อีเมล'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">รหัส 6 หลักจากอีเมล <span className="req">*</span></label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            className="steam-input"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="กรอกรหัส 6 หลัก"
                                            required
                                            autoFocus
                                        />
                                        <span className="form-hint-inline">
                                            ไม่ได้รับอีเมล? ตรวจในโฟลเดอร์ Junk/Spam หรือ{' '}
                                            <button type="button" className="register-link" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={handleSendCode} disabled={sendingCode}>
                                                ส่งรหัสอีกครั้ง
                                            </button>
                                        </span>
                                    </div>
                                    <div className="register-grid">
                                        <div className="form-group">
                                            <label className="form-label">รหัสผ่านใหม่ <span className="req">*</span></label>
                                            <input
                                                type="password"
                                                autoComplete="new-password"
                                                className="steam-input"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="อย่างน้อย 4 ตัวอักษร"
                                                minLength="4"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">ยืนยันรหัสผ่านใหม่ <span className="req">*</span></label>
                                            <input
                                                type="password"
                                                autoComplete="new-password"
                                                className="steam-input"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <button type="submit" className="steam-btn steam-btn-primary login-btn" disabled={loading}>
                                        {loading ? 'กำลังบันทึก...' : 'ตั้งรหัสผ่านใหม่'}
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                className="steam-btn steam-btn-secondary login-btn"
                                style={{ marginTop: 8 }}
                                onClick={() => { setMethod(null); setError(''); setInfo(''); }}
                            >
                                เลือกวิธีอื่น
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleSubmit} className="login-form">
                            {error && (
                                <div className="alert alert-error">
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                    </svg>
                                    {error}
                                </div>
                            )}

                            <div className="alert alert-success" style={{ marginBottom: 12 }}>
                                พบบัญชีของเลขบัตร <strong>{cid}</strong>{maskedName ? <> — ชื่อ <strong>{maskedName}</strong></> : ''} กรุณายืนยันตัวตนเพื่อขอเปลี่ยนรหัส
                            </div>

                            {/* คำชี้แจงการยืนยันตัวตน (ป้องกันการแอบอ้างสิทธิ์) */}
                            <IdentityVerifyNotice requireFacePhoto={requireFacePhoto} />

                            {/* === รูปถ่ายตนเองถือบัตรประชาชน (บังคับเฉพาะโหมด face_card — ตั้งค่าในหน้าตั้งค่าระบบ) === */}
                            {requireFacePhoto && (
                            <div className="form-group">
                                <label className="form-label">1) รูปถ่ายตนเองถือบัตรประชาชน <span className="req">*</span></label>
                                <div className="idcard-instruction">
                                    ถือบัตรไว้ระดับอกใต้ใบหน้า <strong>หันด้านหน้าบัตร (ด้านที่มีรูปหน้า) เข้ากล้อง</strong> ถ่ายในที่แสงสว่าง —
                                    เห็น<strong>ใบหน้าชัดเจน</strong>และ<strong>ข้อมูลในบัตรอ่านได้</strong> (ตอนถ่ายจะมีกรอบช่วยจัดตำแหน่งให้)
                                </div>
                                {facePreview && (
                                    <div className="idcard-preview">
                                        <img src={facePreview} alt="ตัวอย่างรูปถือบัตรที่อัปโหลด" />
                                        <div className="idcard-preview-actions">
                                            <button type="button" className="steam-btn steam-btn-danger" onClick={clearFace}>ลบรูป</button>
                                        </div>
                                    </div>
                                )}
                                <ImageCapture
                                    onSelect={handleFaceSelect}
                                    accept="image/*"
                                    facingMode="user"
                                    overlay="holdcard"
                                    check={photoCheckEnabled ? 'holdcard' : undefined}
                                    captureLabel={facePreview ? 'ถ่ายใหม่' : 'ถ่ายรูปถือบัตร'}
                                    browseLabel="เลือกรูปจากเครื่อง"
                                />
                            </div>
                            )}

                            {/* === ถ่ายรูปบัตรประชาชน (บังคับ) === */}
                            <div className="form-group">
                                <label className="form-label">{requireFacePhoto ? '2) ' : ''}รูปด้านหน้าบัตรประจำตัวประชาชน (เฉพาะบัตร) <span className="req">*</span></label>
                                <div className="idcard-instruction">
                                    <strong>ถ่าย "ด้านหน้าบัตร" เท่านั้น</strong> — ด้านที่มีรูปหน้า เลขบัตร 13 หลัก และชื่อ-นามสกุล
                                    (❌ ไม่ใช่ด้านหลังบัตร) ถ่ายให้ชัด เพื่อยืนยันว่าเป็นเจ้าของบัญชี
                                </div>
                                {idCardPreview && (
                                    <div className="idcard-preview">
                                        <img src={idCardPreview} alt="ตัวอย่างบัตรที่อัปโหลด" />
                                        <div className="idcard-preview-actions">
                                            <button type="button" className="steam-btn steam-btn-danger" onClick={clearIdCard}>ลบรูป</button>
                                        </div>
                                    </div>
                                )}
                                <ImageCapture
                                    onSelect={handleIdCardSelect}
                                    check={photoCheckEnabled ? 'idcard' : undefined}
                                    accept="image/*"
                                    captureLabel={idCardPreview ? 'ถ่ายใหม่' : 'ถ่ายรูปบัตร'}
                                    browseLabel="เลือกรูปบัตรจากเครื่อง"
                                />
                                <div className="idcard-privacy">
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    <span>รูปบัตรเก็บไว้อย่างปลอดภัย เข้าถึงได้เฉพาะเจ้าหน้าที่ที่ได้รับอนุญาต เพื่อยืนยันตัวตนเท่านั้น</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">เบอร์โทรที่เคยให้ไว้ <span className="req">*</span></label>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    className="steam-input"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    placeholder="เช่น 0812345678"
                                    required
                                />
                                <span className="form-hint-inline">ใช้ยืนยันตัวตนกับข้อมูลที่เคยให้เจ้าหน้าที่ไว้</span>
                            </div>

                            <div className="register-grid">
                                <div className="form-group">
                                    <label className="form-label">รหัสผ่านใหม่ <span className="req">*</span></label>
                                    <input
                                        type="password"
                                        className="steam-input"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="อย่างน้อย 4 ตัวอักษร"
                                        minLength="4"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">ยืนยันรหัสผ่านใหม่ <span className="req">*</span></label>
                                    <input
                                        type="password"
                                        className="steam-input"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
                                        required
                                    />
                                </div>
                            </div>
                            <span className="form-hint-inline">รหัสผ่านใหม่จะถูกซ่อนไว้ เจ้าหน้าที่จะไม่เห็นค่ารหัส — ระบบจะเปลี่ยนให้เมื่อได้รับอนุมัติ</span>

                            <div className="form-group" style={{ marginTop: 12 }}>
                                <label className="form-label">หมายเหตุ (ถ้ามี)</label>
                                <textarea
                                    className="steam-input"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="ข้อมูลเพิ่มเติมถึงเจ้าหน้าที่ (ถ้ามี)"
                                    rows="2"
                                    maxLength="500"
                                />
                            </div>

                            <button type="submit" className="steam-btn steam-btn-primary login-btn" disabled={loading}>
                                {loading ? (
                                    <>
                                        <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                                        กำลังส่งคำขอ...
                                    </>
                                ) : 'ส่งคำขอเปลี่ยนรหัสผ่าน'}
                            </button>
                            <button
                                type="button"
                                className="steam-btn steam-btn-secondary login-btn"
                                style={{ marginTop: 8 }}
                                onClick={() => {
                                    if (canEmail) { setMethod(null); setError(''); } else { backToStep1(); }
                                }}
                            >
                                ย้อนกลับ
                            </button>
                        </form>
                    )}

                    <div className="login-footer">
                        <p>นึกรหัสออกแล้ว? <Link to="/login" className="register-link">กลับไปเข้าสู่ระบบ</Link></p>
                        <p style={{ fontSize: '12px', marginTop: '4px', color: '#9ca3af' }}>
                            บัญชีที่ยืนยันอีเมลแล้วรีเซ็ตเองได้ทันที — นอกนั้นเจ้าหน้าที่จะตรวจสอบหลักฐานก่อนเปลี่ยนรหัสให้
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
